import { CampaignClass } from '@feathr/blackbox';

/**
 * The wizard steps a Google Ad Grants campaign can render. Smart campaigns edit
 * keywords and ad text in the separate `keywords` and `adText` steps; Search
 * campaigns fold both into a single `ads` step.
 */
export type TGoogleAdsStepKey =
  | 'information'
  | 'location'
  | 'keywords'
  | 'adText'
  | 'ads'
  | 'goal'
  | 'budget'
  | 'review';

/** Wizard indices the Review step's "edit" buttons jump to, resolved per class. */
export interface IReviewStepIndices {
  information: number;
  location: number;
  keywords: number;
  adText: number;
  /** Where a Search campaign's ad group cards jump to. `-1` for a Smart campaign. */
  ads: number;
  goal: number;
  budget: number;
}

/**
 * Google's per-asset limits for responsive search ad text, shared by the Smart
 * wizard's Ad Text step and the Search wizard's Ads step.
 *
 * @see https://support.google.com/google-ads/answer/7684791
 */
export const AD_TEXT_LIMITS = {
  description: {
    maxItems: 4,
    minItems: 2,
    maxLength: 90,
  },
  headline: {
    maxItems: 15,
    minItems: 3,
    maxLength: 30,
  },
} as const;

const SMART_STEP_KEYS: readonly TGoogleAdsStepKey[] = [
  'information',
  'location',
  'keywords',
  'adText',
  'goal',
  'budget',
  'review',
];

const SEARCH_STEP_KEYS: readonly TGoogleAdsStepKey[] = [
  'information',
  'location',
  'ads',
  'goal',
  'budget',
  'review',
];

export function isGoogleAdsSearchClass(cls: CampaignClass): boolean {
  return cls === CampaignClass.GoogleAdsSearch;
}

/**
 * Google Ad Grants Smart and Search campaigns share one wizard. This returns the
 * ordered step keys for the given class so the wizard, its progress tabs, and its
 * validation all derive from a single source of truth.
 */
export function getGoogleAdsStepKeys(cls: CampaignClass): TGoogleAdsStepKey[] {
  return [...(isGoogleAdsSearchClass(cls) ? SEARCH_STEP_KEYS : SMART_STEP_KEYS)];
}

/**
 * Resolve the wizard indices the Review step's "edit" buttons jump to. For Search
 * campaigns the keyword and ad-text edits both land on the combined `ads` step.
 */
export function getReviewStepIndices(cls: CampaignClass): IReviewStepIndices {
  const keys = getGoogleAdsStepKeys(cls);
  const isSearch = isGoogleAdsSearchClass(cls);
  return {
    information: keys.indexOf('information'),
    location: keys.indexOf('location'),
    keywords: keys.indexOf(isSearch ? 'ads' : 'keywords'),
    adText: keys.indexOf(isSearch ? 'ads' : 'adText'),
    ads: keys.indexOf('ads'),
    goal: keys.indexOf('goal'),
    budget: keys.indexOf('budget'),
  };
}
